import { useState, useEffect, useRef } from 'react';
import { useVoiceStore } from '../../stores/voiceStore.js';

// ── Elapsed timer ─────────────────────────────────────────────────────────────
function useElapsed(startTs) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!startTs) { setSecs(0); return; }
    const tick = () => setSecs(Math.floor((Date.now() - startTs) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTs]);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Speaking indicator (animated rings) ──────────────────────────────────────
function SpeakingRing({ isSpeaking, size = 32 }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {isSpeaking && (
        <div style={{
          position: 'absolute', inset: -3, borderRadius: '50%',
          border: '2px solid #22c55e',
          animation: 'voicePulse 1s ease-in-out infinite',
          opacity: 0.8,
        }} />
      )}
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: 'linear-gradient(135deg,#22c55e,#16a34a)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.38, fontWeight: 700, color: '#fff',
        border: isSpeaking ? '2px solid #22c55e' : '2px solid rgba(255,255,255,0.1)',
      }}>
        {/* filled by parent */}
      </div>
    </div>
  );
}

// ── HUD button ────────────────────────────────────────────────────────────────
function HudBtn({ icon, label, active, danger, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        padding: '6px 10px', borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
        background: active
          ? (danger ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.15)')
          : 'rgba(255,255,255,0.06)',
        border: `1px solid ${active ? (danger ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.3)') : 'rgba(255,255,255,0.1)'}`,
        color: active ? (danger ? '#ef4444' : '#22c55e') : 'rgba(255,255,255,0.7)',
        transition: 'all 0.15s', minWidth: 48, opacity: disabled ? 0.4 : 1,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = active ? (danger ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.25)') : 'rgba(255,255,255,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? (danger ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.15)') : 'rgba(255,255,255,0.06)'; }}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

// ── Participant tile (mini) ────────────────────────────────────────────────────
function ParticipantTile({ p }) {
  const initial = (p.name || '?')[0].toUpperCase();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <div style={{ position: 'relative' }}>
        {p.isSpeaking && (
          <div style={{ position: 'absolute', inset: -2, borderRadius: '50%', border: '2px solid #22c55e', animation: 'voicePulse 1s ease-in-out infinite' }} />
        )}
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: p.isLocal ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'linear-gradient(135deg,#06b6d4,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>
          {p.avatar || initial}
        </div>
        {p.isMuted && (
          <div style={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, border: '1px solid rgba(0,0,0,0.5)' }}>🔇</div>
        )}
      </div>
      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
        {p.isLocal ? 'You' : p.name}
      </span>
    </div>
  );
}

// ── Main VoiceHUD ─────────────────────────────────────────────────────────────
export default function VoiceHUD() {
  const channelId       = useVoiceStore(s => s.channelId);
  const channelName     = useVoiceStore(s => s.channelName);
  const isMuted         = useVoiceStore(s => s.isMuted);
  const isDeafened      = useVoiceStore(s => s.isDeafened);
  const isCameraOn      = useVoiceStore(s => s.isCameraOn);
  const isSharingScreen = useVoiceStore(s => s.isSharingScreen);
  const participants    = useVoiceStore(s => s.participants);
  const joinedAt        = useVoiceStore(s => s.joinedAt);
  const toggleMute      = useVoiceStore(s => s.toggleMute);
  const toggleDeafen    = useVoiceStore(s => s.toggleDeafen);
  const toggleCamera    = useVoiceStore(s => s.toggleCamera);
  const toggleScreen    = useVoiceStore(s => s.toggleScreenShare);
  const leave           = useVoiceStore(s => s.leave);

  const elapsed = useElapsed(joinedAt);

  if (!channelId) return null;

  return (
    <>
      <style>{`
        @keyframes voicePulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.15); opacity: 0.4; }
        }
      `}</style>
      <div style={{
        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
        zIndex: 800, display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(10,10,14,0.95)', backdropFilter: 'blur(16px)',
        border: '1px solid rgba(34,197,94,0.25)', borderRadius: 14,
        padding: '10px 14px', boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(34,197,94,0.1)',
        maxWidth: 'calc(100vw - 40px)',
      }}>
        {/* Status & channel info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 90, paddingRight: 10, borderRight: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'voicePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 700 }}>Voice Connected</span>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🔊 {channelName || 'Voice Channel'}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>{elapsed}</div>
        </div>

        {/* Participant tiles */}
        {participants.length > 0 && (
          <div style={{ display: 'flex', gap: 6, paddingRight: 10, borderRight: '1px solid rgba(255,255,255,0.07)', flexWrap: 'nowrap', overflow: 'hidden', maxWidth: 180 }}>
            {participants.slice(0, 5).map(p => (
              <ParticipantTile key={p.id} p={p} />
            ))}
            {participants.length > 5 && (
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>+{participants.length - 5}</div>
            )}
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', gap: 4 }}>
          <HudBtn icon={isMuted ? '🔇' : '🎤'} label={isMuted ? 'Unmute' : 'Mute'} active={isMuted} onClick={toggleMute} />
          <HudBtn icon={isDeafened ? '🔕' : '🔊'} label={isDeafened ? 'Undeafen' : 'Deafen'} active={isDeafened} onClick={toggleDeafen} />
          <HudBtn icon='📷' label={isCameraOn ? 'Cam On' : 'Camera'} active={isCameraOn} onClick={toggleCamera} />
          <HudBtn icon='🖥️' label={isSharingScreen ? 'Sharing' : 'Share'} active={isSharingScreen} onClick={toggleScreen} />
          <HudBtn icon='📞' label='Leave' danger active onClick={leave} />
        </div>
      </div>
    </>
  );
}
